/**
 * Redux reducer for individual assets.
 * @module redux/reducers/asset
 */

import update from "immutability-helper";
import Settings from "../settings";
import {
  ASSET_SELECTED,
  LOAD_ASSET_REQUEST,
  ASSET_LOAD_REQUEST_FAILED,
  ASSET_LOADED,
  CLEAR_ASSET,
  TEMPLATE_DEFINITION_LOADED,
  CHANNEL_LIST_RECEIVED,
  AUTOMATION_TRANSITIONS_RECEIVED,
  UPDATE_REPLACEABLE_VALUE,
  UPDATE_TEMPLATE_PROPERTY,
  INSERT_MOS_OBJECT_REQUEST,
  INSERT_MOS_OBJECT_FAILED,
  INSERT_MOS_OBJECT_SUCCESS,
  GENERATE_PREVIEW,
  GENERATE_PREVIEW_FAILED,
  PREVIEW_RECEIVED,
  SELECT_REPLACEABLE,
  SET_AUTOFILL_DATA,
  SET_AUTOFILL_COLUMN_DATA,
  LOAD_AUTOFILL_REQUEST,
  END_EDIT_ASSET,
  TOGGLE_REPLACEABLE_USE_DATA_SOURCE,
  UPDATE_AUTOFILL_REPLACEABLE_VALUES,
  UPDATE_DATA_BOUND_REPLACEABLE_VALUES,
  BEGIN_TEMPLATE_SWAP,
  END_TEMPLATE_SWAP,
  SWAP_TEMPLATE_REPLACEABLE_VALUES,
  REQUEST_PRIME_STYLES,
  REQUEST_PRIME_STYLES_FAILED,
  PRIME_STYLES_RECEIVED,
  PRIME_STYLE_CHANGED,
  RESTORE_STATE
} from "../actions/asset";
import { CREATED_DEFICIENT_ASSET } from "../actions/axisDeficientAssetUrl";
import { SUBMIT_ORDER_SUCCEEDED } from "../actions/axisOrder";
import { DELIVERY_FEED_RECEIVED } from "../actions/axisOrders";
import { TIMELINE_REMOVE_GRAPHIC } from "../actions/clipperMovie";

const debug = process.env.NODE_ENV === "development";

const initialState = {
  selectedAssetId: null,
  asset: {},
  templateDefinition: {},
  isMosObject: false,
  assetLoading: false,
  channelList: [],
  automationTransitionList: [],
  selectedAutomationTransition: "",
  generatingPreview: false,
  previewError: null,
  selectedReplaceableId: null,
  autofillData: {},
  autofillColumnData: {},
  autofillLoading: false,
  editing: false,
  axisId: null,
  inserting: false,
  insertFailed: false,
  previousMosObject: null,
  swapping: false,
  templateSwapping: false,
  insertedObjId: null,
  insertedMosXml: null,
  primeStylesList: [],
  primeStylesLoading: false,
  primeStyle: ""
};

const ACTION_HANDLERS = {
  [ASSET_SELECTED]: (state, action) => ({
    ...state,
    selectedAssetId: action.payload.selectedAsset.id || null,
    asset: action.payload.selectedAsset,
    isMosObject: !!action.payload.selectedAsset.mosObjectId,
    selectedReplaceableId: null,
    editing: !!action.payload.editing,
    previewError: null,
    previousMosObject: action.payload.previousMosObject,
    swapping: !!action.payload.swapping
  }),
  [CLEAR_ASSET]: state => ({
    ...state,
    selectedAssetId: null,
    asset: {},
    templateDefinition: {},
    selectedReplaceableId: null,
    previousMosObject: null,
    swapping: false,
    templateSwapping: false
  }),
  [LOAD_ASSET_REQUEST]: state => ({
    ...state,
    assetLoading: true,
    insertedMosXml: null,
    insertedObjId: null
  }),
  [ASSET_LOAD_REQUEST_FAILED]: state => ({
    ...state,
    assetLoading: false
  }),
  [ASSET_LOADED]: state => ({
    ...state,
    assetLoading: false,
    previewError: null
  }),
  [TEMPLATE_DEFINITION_LOADED]: (state, action) => {
    const { templateDefinition } = action.payload;
    const autofillGroupMap = new Map();
    const autofillPrimarySourceMap = new Map();
    let updatedState = {
      ...state,
      templateDefinition: {
        ...templateDefinition,
        mediaReplaceables: templateDefinition.mediaReplaceables.map(r =>
          setAutofillGroup(r, autofillGroupMap, autofillPrimarySourceMap)
        ),
        textReplaceables: templateDefinition.textReplaceables.map(r =>
          setAutofillGroup(r, autofillGroupMap, autofillPrimarySourceMap)
        )
      },
      assetLoading: false,
      channelList:
        templateDefinition.channelList &&
        templateDefinition.channelList.length > 0
          ? templateDefinition.channelList.map(channels => ({
              ...channels,
              id: parseInt(channels.id, 10)
            }))
          : state.channelList,
      previewError: null
    };

    if (
      templateDefinition.primeStyleList &&
      templateDefinition.primeStyleList.length > 0
    ) {
      let listUpdated = false;
      if (
        !state.primeStylesList ||
        templateDefinition.primeStyleList.length !==
          state.primeStylesList.length
      ) {
        listUpdated = true;
      } else {
        for (let i = 0; i < templateDefinition.primeStyleList.length; i++) {
          if (
            templateDefinition.primeStyleList[i].id !==
            state.primeStylesList[i].id
          ) {
            listUpdated = true;
            break;
          }
        }
      }

      if (listUpdated) {
        // reset prime style in case the same style name exists in multiple contexts
        updatedState = {
          ...updatedState,
          primeStyle: null,
          primeStylesList: templateDefinition.primeStyleList
        };
      }
    }

    if (templateDefinition.primeStyle != null) {
      updatedState = {
        ...updatedState,
        primeStyle: templateDefinition.primeStyle
      };
    }

    return updatedState;
  },
  [CHANNEL_LIST_RECEIVED]: (state, action) => ({
    ...state,
    channelList: action.payload.channelList
  }),
  [AUTOMATION_TRANSITIONS_RECEIVED]: (state, action) => {
    const automationTransitionList = action.payload.automationTransitionList;
    return {
      ...state,
      automationTransitionList,
      templateDefinition: {
        ...state.templateDefinition,
        properties: {
          ...state.templateDefinition.properties,
          "automation transition":
            automationTransitionList && automationTransitionList.length > 0
              ? action.payload.automationTransitionList[0].value
              : ""
        }
      }
    };
  },
  [UPDATE_REPLACEABLE_VALUE]: (state, action) => {
    const { type, id, name, value, url, provider } = action.payload;
    const replaceableType =
      type === "text" ? "textReplaceables" : "mediaReplaceables";
    const index = state.templateDefinition[replaceableType].findIndex(
      r => r.id === id
    );
    if (type === "text" || !value) {
      return update(state, {
        insertedObjId: { $set: null },
        insertedMosXml: { $set: null },
        templateDefinition: {
          [replaceableType]: {
            [index]: {
              id: { $set: id },
              name: { $set: name },
              type: { $set: type },
              url: { $set: url },
              value: { $set: value },
              provider: { $set: provider },
              dirty: { $set: true }
            }
          }
        }
      });
    }
    return {
      ...state,
      insertedObjId: null,
      insertedMosXml: null,
      templateDefinition: {
        ...state.templateDefinition,
        mediaReplaceables: state.templateDefinition.mediaReplaceables.map(r => {
          if (r.id === id) {
            return {
              ...r,
              url,
              value,
              provider,
              dirty: true
            };
          }

          return r;
        }),
        textReplaceables: state.templateDefinition.textReplaceables.map(r => {
          if (r.properties.dataSource === id) {
            return {
              ...r,
              properties: {
                ...r.properties,
                usesDataSource: true
              }
            };
          }

          return r;
        })
      }
    };
  },
  [UPDATE_TEMPLATE_PROPERTY]: (state, action) => {
    if (state.templateDefinition.properties) {
      return update(state, {
        templateDefinition: {
          properties: {
            [action.payload.key]: { $set: action.payload.value }
          }
        }
      });
    }
    return state;
  },
  [INSERT_MOS_OBJECT_REQUEST]: state => ({
    ...state,
    inserting: true,
    insertedObjId: null,
    insertedMosXml: null,
    insertFailed: false
  }),
  [INSERT_MOS_OBJECT_FAILED]: state => ({
    ...state,
    inserting: false,
    insertedObjId: null,
    insertedMosXml: null,
    insertFailed: true
  }),
  [INSERT_MOS_OBJECT_SUCCESS]: (state, action) => ({
    ...state,
    inserting: false,
    insertedObjId: action.payload.objId,
    insertedMosXml: action.payload.mosXml,
    insertFailed: false
  }),
  [GENERATE_PREVIEW]: state => ({
    ...state,
    generatingPreview: true,
    previewError: null
  }),
  [GENERATE_PREVIEW_FAILED]: (state, action) => ({
    ...state,
    generatingPreview: false,
    previewError: action.payload.error
  }),
  [PREVIEW_RECEIVED]: (state, action) =>
    update(state, {
      generatingPreview: { $set: false },
      previewError: { $set: null },
      asset: {
        previewImg: { $set: action.payload.previewImg }
      }
    }),
  [SELECT_REPLACEABLE]: (state, action) => ({
    ...state,
    selectedReplaceableId: action.payload.id
  }),
  [LOAD_AUTOFILL_REQUEST]: state => ({
    ...state,
    autofillLoading: true,
    autofillData: [],
    autofillColumnData: []
  }),
  [SET_AUTOFILL_DATA]: (state, action) => ({
    ...state,
    autofillLoading: false,
    autofillData: action.payload.data
  }),
  [SET_AUTOFILL_COLUMN_DATA]: (state, action) => ({
    ...state,
    autofillLoading: false,
    autofillColumnData: action.payload.data
  }),
  [CREATED_DEFICIENT_ASSET]: (state, action) => {
    const { id, name, type } = action.payload.replaceable;
    const replaceableType = "mediaReplaceables";
    const index = state.templateDefinition[replaceableType].findIndex(
      r => r.id === id
    );
    return update(state, {
      templateDefinition: {
        [replaceableType]: {
          [index]: {
            id: { $set: id },
            name: { $set: name },
            type: { $set: type },
            url: { $set: "images/waiting_delivery_thumbnail.png" },
            value: { $set: action.payload.assetId },
            axisId: { $set: action.payload.axisId }
          }
        }
      }
    });
  },
  [DELIVERY_FEED_RECEIVED]: (state, action) => {
    if (state.templateDefinition.mediaReplaceables) {
      return {
        ...state,
        templateDefinition: {
          ...state.templateDefinition,
          mediaReplaceables: state.templateDefinition.mediaReplaceables.map(
            replaceable => {
              const updatedAsset = action.payload.feed.find(
                axisAsset => axisAsset.axisId === replaceable.axisId
              );
              if (updatedAsset) {
                return {
                  ...replaceable,
                  url: debug
                    ? updatedAsset.previewUrl.replace(
                        "../",
                        Settings.camioServer
                      )
                    : updatedAsset.previewUrl
                };
              }
              return replaceable;
            }
          )
        }
      };
    }
    return state;
  },
  [SUBMIT_ORDER_SUCCEEDED]: (state, action) => ({
    ...state,
    templateDefinition: {
      ...state.templateDefinition,
      mediaReplaceables: state.templateDefinition.mediaReplaceables.map(
        replaceable => {
          const updatedItem = action.payload.items.find(
            item => item.replaceableId === replaceable.id
          );
          if (updatedItem) {
            return {
              ...replaceable,
              url: "images/waiting_delivery_thumbnail.png",
              value: updatedItem.assetId,
              axisId: updatedItem.uuid
            };
          }
          return replaceable;
        }
      )
    }
  }),
  [END_EDIT_ASSET]: () => ({
    ...initialState
  }),
  [TIMELINE_REMOVE_GRAPHIC]: (state, action) => {
    if (state.mosObjectId === action.payload.mosObjectObjectId) {
      return {
        ...initialState
      };
    }

    return state;
  },
  [TOGGLE_REPLACEABLE_USE_DATA_SOURCE]: (state, action) => {
    const { id } = action.payload;
    return {
      ...state,
      templateDefinition: {
        ...state.templateDefinition,
        textReplaceables: state.templateDefinition.textReplaceables.map(r => {
          if (r.id === id) {
            return {
              ...r,
              properties: {
                ...r.properties,
                usesDataSource: !r.properties.usesDataSource
              }
            };
          }

          return r;
        })
      }
    };
  },
  [UPDATE_AUTOFILL_REPLACEABLE_VALUES]: (state, action) => {
    const { updatedReplaceables } = action.payload;
    return {
      ...state,
      templateDefinition: {
        ...state.templateDefinition,
        mediaReplaceables: state.templateDefinition.mediaReplaceables.map(r => {
          const updatedReplaceable = updatedReplaceables.find(
            updated => updated.id === r.id
          );
          if (updatedReplaceable) {
            return {
              ...r,
              url: updatedReplaceable.url,
              value: updatedReplaceable.value
            };
          }

          return r;
        }),
        textReplaceables: state.templateDefinition.textReplaceables.map(r => {
          const updatedReplaceable = updatedReplaceables.find(
            updated => updated.id === r.id
          );
          if (updatedReplaceable) {
            return {
              ...r,
              value: updatedReplaceable.value
            };
          }

          return r;
        })
      }
    };
  },
  [UPDATE_DATA_BOUND_REPLACEABLE_VALUES]: (state, action) => {
    const { values } = action.payload;
    return {
      ...state,
      templateDefinition: {
        ...state.templateDefinition,
        textReplaceables: state.templateDefinition.textReplaceables.map(r => {
          const value = values.find(
            v => v.id.toLowerCase() === r.id.toLowerCase()
          );
          if (value && r.properties.usesDataSource) {
            return {
              ...r,
              value: value.value
            };
          }

          return r;
        })
      }
    };
  },
  [BEGIN_TEMPLATE_SWAP]: state => ({
    ...state,
    assetLoading: true,
    templateSwapping: true
  }),
  [END_TEMPLATE_SWAP]: state => ({
    ...state,
    assetLoading: false,
    templateSwapping: false
  }),
  [SWAP_TEMPLATE_REPLACEABLE_VALUES]: (state, action) => {
    const { replaceables: sourceReplaceables } = action.payload;
    const sourceTextReplaceables = sourceReplaceables.filter(
      r => r.type === "text"
    );
    const sourceImageReplaceables = sourceReplaceables.filter(
      r => r.type === "image"
    );
    const sourceVideoReplaceables = sourceReplaceables.filter(
      r => r.type === "video"
    );

    const {
      mediaReplaceables: destMediaReplaceables,
      textReplaceables: destTextReplaceables
    } = state.templateDefinition;
    const textReplaceables = getSwappedReplaceables(
      sourceTextReplaceables,
      destTextReplaceables
    );
    const imageReplaceables = getSwappedReplaceables(
      sourceImageReplaceables,
      destMediaReplaceables.filter(r => r.type === "image")
    );
    const videoReplaceables = getSwappedReplaceables(
      sourceVideoReplaceables,
      destMediaReplaceables.filter(r => r.type === "video")
    );

    const mediaReplaceables = (imageReplaceables || [])
      .concat(videoReplaceables)
      .sort((a, b) =>
        a.properties["display index"] > b.properties["display index"] ? 1 : -1
      );

    return {
      ...state,
      templateDefinition: {
        ...state.templateDefinition,
        mediaReplaceables,
        textReplaceables
      }
    };
  },
  [REQUEST_PRIME_STYLES]: state => ({
    ...state,
    primeStylesLoading: true
  }),
  [REQUEST_PRIME_STYLES_FAILED]: state => ({
    ...state,
    primeStylesLoading: false
  }),
  [PRIME_STYLES_RECEIVED]: (state, action) => ({
    ...state,
    primeStylesLoading: false,
    primeStyle: action.payload.primeStyle || "",
    primeStylesList: action.payload.primeStyles
  }),
  [PRIME_STYLE_CHANGED]: (state, action) => ({
    ...state,
    primeStyle: action.payload.primeStyle || "",
    insertedMosXml: null
  }),
  [RESTORE_STATE]: (state, action) => ({
    ...state,
    primeStyle: action.payload.primeStyle || state.primeStyle || ""
  })
};

const getSwappedReplaceables = (sourceReplaceables, destReplaceables) => {
  const replaceables = [];
  if (sourceReplaceables.length > 0) {
    const visibleSourceReplaceables = sourceReplaceables
      .filter(r => !r.properties["read only"])
      .sort((a, b) =>
        a.properties["display index"] > b.properties["display index"] ? 1 : -1
      );
    const visibleDestReplaceables = destReplaceables
      .filter(r => !r.properties["read only"])
      .sort((a, b) =>
        a.properties["display index"] > b.properties["display index"] ? 1 : -1
      );

    for (let i = 0; i < visibleDestReplaceables.length; i++) {
      if (i >= visibleSourceReplaceables.length) {
        break;
      }
      replaceables.push(
        updateReplaceableData(
          visibleDestReplaceables[i],
          visibleSourceReplaceables[i]
        )
      );
    }

    // Hidden has to be done separately because the sorting is different.
    // Hidden fields do not have a proper display index, you must sort on id.
    const hiddenSourceReplaceables = sourceReplaceables
      .filter(r => !!r.properties["read only"])
      .sort((a, b) => (a.id > b.id ? 1 : -1));
    const hiddenDestReplaceables = destReplaceables
      .filter(r => !!r.properties["read only"])
      .sort((a, b) => (a.id > b.id ? 1 : -1));

    for (let i = 0; i < hiddenDestReplaceables.length; i++) {
      if (i >= hiddenSourceReplaceables.length) {
        break;
      }
      replaceables.push(
        updateReplaceableData(
          hiddenDestReplaceables[i],
          hiddenSourceReplaceables[i]
        )
      );
    }
  }

  return replaceables;
};

const updateReplaceableData = (r, source) => ({
  ...r,
  properties: {
    ...r.properties,
    usesDataSource: source.properties.usesDataSource
  },
  provider: source.provider,
  url: source.url,
  value: source.value
});

const setAutofillGroup = (r, autofillGroupMap, autofillPrimarySourceMap) => {
  const {
    autofillGroup,
    autofillDataColumnIndex,
    autofillDataListName
  } = r.properties;
  if (
    autofillGroup ||
    !autofillDataListName ||
    autofillDataColumnIndex === -1
  ) {
    return r;
  }

  let newAutofillGroup = 1;
  if (autofillGroupMap.has(autofillDataListName)) {
    if (autofillDataColumnIndex > 0) {
      newAutofillGroup = autofillGroupMap.get(autofillDataListName);
    } else {
      if (autofillPrimarySourceMap.has(autofillDataListName)) {
        // increment
        const currentAutofillGroup = autofillPrimarySourceMap.get(
          autofillDataListName
        );
        autofillPrimarySourceMap.set(
          autofillDataListName,
          currentAutofillGroup + 1
        );
      } else {
        // initialize
        newAutofillGroup = autofillGroupMap.get(autofillDataListName);
        autofillPrimarySourceMap.set(autofillDataListName, newAutofillGroup);
      }
      newAutofillGroup = autofillPrimarySourceMap.get(autofillDataListName);
    }
  } else {
    newAutofillGroup = autofillGroupMap.size + 1;
    autofillGroupMap.set(autofillDataListName, newAutofillGroup);
    if (autofillDataColumnIndex === 0) {
      autofillPrimarySourceMap.set(autofillDataListName, newAutofillGroup);
    }
  }

  return {
    ...r,
    properties: {
      ...r.properties,
      autofillGroup: newAutofillGroup
    }
  };
};

export default function asset(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type];

  return handler ? handler(state, action) : state;
}



// WEBPACK FOOTER //
// ./app/reducers/asset.js